import { BookOpen, IndianRupee, Layers, UserPlus, type LucideIcon } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

interface Step {
  icon: LucideIcon;
  title: string;
  body: string;
}

/**
 * Fixed text on purpose: these describe how the product works, not how any one
 * institute is set up, so nothing here may name a school or a session.
 */
const STEPS: Step[] = [
  {
    icon: Layers,
    title: 'Start with the class and its sections',
    body: 'A class is what a child is admitted into; sections split it into rooms. Each section carries its own seat cap, and the class’s intake is the sum of them.',
  },
  {
    icon: BookOpen,
    title: 'Say what it teaches',
    body: 'Core subjects are taught to everybody. Electives and languages are chosen, so a section may not offer all of them.',
  },
  {
    icon: IndianRupee,
    title: 'Set the year’s fee',
    body: 'Tuition, books and everything else are recorded separately and shown together as the annual fee. Fees can be revised at any point in the session.',
  },
  {
    icon: UserPlus,
    title: 'Allot a class teacher to each section',
    body: 'Teachers come from your staff records, so a section’s teacher stays the same on the timetable, the attendance register and the report card.',
  },
];

export interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Offered from the footer, so the dialog ends where the work starts. */
  onAddClass?: () => void;
}

export function HowItWorksModal({ isOpen, onClose, onAddClass }: HowItWorksModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="How classes setup works"
      description="Four steps, in the order the screen asks for them."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {onAddClass && (
            <Button
              onClick={() => {
                onClose();
                onAddClass();
              }}
            >
              Add a class
            </Button>
          )}
        </>
      }
    >
      <ol className="flex flex-col gap-3">
        {STEPS.map((step, index) => {
          const Icon = step.icon;

          return (
            <li key={step.title} className="flex gap-3 rounded-card border border-line bg-surface-2 p-3.5">
              <span
                className="grid h-9 w-9 shrink-0 place-items-center rounded-card bg-accent-soft text-accent"
                aria-hidden
              >
                <Icon className="h-4 w-4" />
              </span>

              <div className="min-w-0">
                <p className="text-sm font-[650] text-ink">
                  <span className="text-ink-3">{index + 1}.</span> {step.title}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-ink-2">{step.body}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </Modal>
  );
}
